import React, { Component } from "react";
import Form from "react-validation/build/form";
import Input from "react-validation/build/input";
import CheckButton from "react-validation/build/button";
import Select from "react-select";
import { isEmail } from "validator";
import { Link } from "react-router-dom";

import AuthService from "../services/auth.service";
import { SCHOOLS, schoolFilterOption } from "../constants/school-options";
import "../curriculum.css";
import { tr } from "../i18n";

const schoolOptions = SCHOOLS.map((s) => ({ value: s.code, label: s.name, address: s.address }));

// Shows each school's address as a muted second line under its name, so a
// teacher picking from 392 similarly-named schools (many share a county/town
// name) has enough to tell same-named schools apart -- react-select renders
// this for both the open dropdown's option rows and the collapsed selected
// value alike, since formatOptionLabel isn't given a context arg to
// distinguish them.
const formatSchoolOptionLabel = (option) => (
  <div>
    <div>{option.label}</div>
    {option.address && <div style={{ fontSize: "0.85em", color: "#6c757d" }}>{option.address}</div>}
  </div>
);

const required = (value) => {
  if (!value) {
    return (
      <div className="alert alert-danger" role="alert">
        {tr("请填写!")}
      </div>
    );
  }
};

const email = (value) => {
  if (!isEmail(value)) {
    return (
      <div className="alert alert-danger" role="alert">
        {tr("邮件地址不正确")}
      </div>
    );
  }
};

const vusername = (value) => {
  if (value.length < 3 || value.length > 20) {
    return (
      <div className="alert alert-danger" role="alert">
        {tr("用户名应含3至20个字节")}
      </div>
    );
  }
};

const vpassword = (value) => {
  if (value && value.length > 0 && (value.length < 6 || value.length > 40)) {
    return (
      <div className="alert alert-danger" role="alert">
        {tr("密码应含6至40个字节")}
      </div>
    );
  }
};

// Simplified from shinshin's register.component.js -- this app only has three roles
// (teacher/expert/admin) and no school/donor domain, so the dozens of school-scoped fields
// (schoolId, title, wechat, contactOnly, ...) are dropped. Self-signup is teacher-only
// (enforced server-side too, see verifySignUp.checkOnlyTeacherRole) -- 专家/管理员 accounts
// are created for someone by an existing admin instead -- so there's no role picker here,
// just a school dropdown (migrated from shinshin's `schools` table). Accounts are usable
// immediately on success -- there is no email-verification step.
export default class Register extends Component {
  constructor(props) {
    super(props);
    this.handleRegister = this.handleRegister.bind(this);
    this.onChangeUsername = this.onChangeUsername.bind(this);
    this.onChangeChineseName = this.onChangeChineseName.bind(this);
    this.onChangeEmail = this.onChangeEmail.bind(this);
    this.onChangePassword = this.onChangePassword.bind(this);
    this.onChangeSchool = this.onChangeSchool.bind(this);

    this.state = {
      username: "",
      chineseName: "",
      email: "",
      password: "",
      school: null,
      successful: false,
      message: "",
    };
  }

  onChangeUsername(e) {
    this.setState({ username: e.target.value });
  }

  onChangeChineseName(e) {
    this.setState({ chineseName: e.target.value });
  }

  onChangeEmail(e) {
    this.setState({ email: e.target.value });
  }

  onChangePassword(e) {
    this.setState({ password: e.target.value });
  }

  onChangeSchool(option) {
    this.setState({ school: option });
  }

  handleRegister(e) {
    e.preventDefault();

    this.setState({ message: "", successful: false });

    this.form.validateAll();

    // react-select's Select isn't a react-validation Input, so it's not
    // covered by validateAll() above -- checked separately.
    if (!this.state.school) {
      this.setState({ successful: false, message: tr("请选择所在学校。") });
      return;
    }

    if (this.checkBtn.context._errors.length === 0) {
      AuthService.signup({
        username: this.state.username,
        email: this.state.email,
        password: this.state.password,
        roles: ["teacher"],
        chineseName: this.state.chineseName,
        schoolCode: this.state.school.value,
      }).then(
        () => {
          this.setState({
            message: tr("账号创建成功，请登录。"),
            successful: true,
          });
        },
        (error) => {
          const resMessage =
            (error.response && error.response.data && error.response.data.message) || error.message || error.toString();

          this.setState({ successful: false, message: resMessage });
        }
      );
    }
  }

  render() {
    return (
      <div className="auth-page">
        {this.state.successful ? (
          <div className="auth-card">
            <div className="auth-badge">
              <i className="fas fa-check-circle"></i>
            </div>
            <h2 className="auth-title">{tr("注册成功")}</h2>
            <p className="auth-subtitle">{this.state.message}</p>
            <Link to="/login">
              <button className="auth-btn-primary">{tr("前往登录")}</button>
            </Link>
          </div>
        ) : (
          <div className="auth-card">
            <div className="auth-badge">
              <i className="fas fa-user-plus"></i>
            </div>
            <h2 className="auth-title">{tr("创建账号")}</h2>
            <p className="auth-subtitle">{tr("加入乡土智课系统")}</p>

            <Form
              onSubmit={this.handleRegister}
              ref={(c) => {
                this.form = c;
              }}
            >
              <div className="form-group">
                <label htmlFor="username">
                  {tr("用户名")}<span className="required">*</span>
                </label>
                <Input
                  type="text"
                  className="form-control"
                  name="username"
                  value={this.state.username}
                  onChange={this.onChangeUsername}
                  validations={[required, vusername]}
                />
              </div>

              <div className="form-group">
                <label htmlFor="chineseName">
                  {tr("姓名")}<span className="required">*</span>
                </label>
                <Input
                  type="text"
                  className="form-control"
                  name="chineseName"
                  value={this.state.chineseName}
                  onChange={this.onChangeChineseName}
                  validations={[required]}
                />
              </div>

              <div className="form-group">
                <label htmlFor="email">
                  {tr("电子邮箱")}<span className="required">*</span>
                </label>
                <Input
                  type="text"
                  className="form-control"
                  name="email"
                  value={this.state.email}
                  onChange={this.onChangeEmail}
                  validations={[required, email]}
                />
              </div>

              <div className="form-group">
                <label htmlFor="password">
                  {tr("密码")}<span className="required">*</span>
                </label>
                <Input
                  type="password"
                  className="form-control"
                  name="password"
                  value={this.state.password}
                  onChange={this.onChangePassword}
                  validations={[required, vpassword]}
                />
              </div>

              <div className="form-group">
                <label htmlFor="school">
                  {tr("所在学校")}<span className="required">*</span>
                </label>
                <Select
                  inputId="school"
                  options={schoolOptions}
                  value={this.state.school}
                  onChange={this.onChangeSchool}
                  placeholder={tr("搜索并选择学校...")}
                  formatOptionLabel={formatSchoolOptionLabel}
                  filterOption={schoolFilterOption}
                />
              </div>

              <div className="form-group">
                <button className="auth-btn-primary">{tr("注册")}</button>
              </div>

              {this.state.message && (
                <div className="form-group">
                  <div className="auth-alert" role="alert">
                    {this.state.message}
                  </div>
                </div>
              )}

              <CheckButton
                style={{ display: "none" }}
                ref={(c) => {
                  this.checkBtn = c;
                }}
              />
            </Form>

            <p className="auth-footnote">
              {tr("已有账号？")} <Link to="/login">{tr("立即登录")}</Link>
            </p>
          </div>
        )}
      </div>
    );
  }
}
